import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BadgeCheck, Copy, Mail, CheckCircle2, XCircle, Loader2, RotateCcw } from 'lucide-react';

const STATUS = {
  pending_confirmation: { label: 'Needs confirming', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  confirmed: { label: 'Confirmed', cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  dismissed: { label: 'Dismissed', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const money = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Online pre-acceptance: send the client link separately, then confirm or dismiss what they picked.
export const OnlineAcceptanceCard = ({ api, jobId, compact = false, onApply, onState }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/appraisals/${jobId}/online-acceptance`); setSt(data); onState && onState(data); }
    catch { setSt(null); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, jobId]);
  useEffect(() => { if (jobId) load(); }, [jobId, load]);

  const link = async (email, reset = false) => {
    if (email && !window.confirm('Email the acceptance link to the client now? This sends a real email.')) return;
    if (reset && !window.confirm('Make a new link? The old link will stop working.')) return;
    setBusy(email ? 'email' : reset ? 'reset' : 'copy');
    try {
      const { data } = await api.post(`/appraisals/${jobId}/online-acceptance/link`, email ? { email: true, base_url: window.location.origin } : { reset });
      const url = `${window.location.origin}${data.path}`;
      if (email) toast.success(data.email?.redirected_to ? `Sent (test mode → ${data.email.redirected_to})` : 'Link emailed to the client');
      else { await navigator.clipboard.writeText(url).catch(() => window.prompt('Copy this link', url)); toast.success('Client link copied'); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create the link'); }
    finally { setBusy(''); }
  };
  const review = async (action) => {
    setBusy(action);
    try { await api.post(`/appraisals/${jobId}/online-acceptance/review`, { action }); toast.success(action === 'confirmed' ? 'Marked as confirmed' : 'Dismissed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };
  if (!st) return null;
  const oa = st.acceptance;
  const s = STATUS[oa?.status];
  return (
    <div data-testid="online-acceptance-card" className={`rounded-lg border p-4 ${oa?.status === 'pending_confirmation' ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200 bg-white'} ${compact ? '' : 'mt-4'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <BadgeCheck size={16} className="text-[#D35400]" />
        <span className="text-sm font-semibold text-slate-800">Online acceptance</span>
        {s && <span data-testid="online-acceptance-status" className={`text-[10px] font-bold uppercase tracking-widest border rounded px-1.5 py-0.5 ${s.cls}`}>{s.label}</span>}
        {!oa && st.link_sent_at && <span className="text-xs text-slate-500">Link emailed {fmt(st.link_sent_at)}</span>}
        <span className="flex-1" />
        <button data-testid="online-acceptance-copy-link" disabled={!!busy} onClick={() => link(false)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50">
          {busy === 'copy' ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} />} Copy client link
        </button>
        <button data-testid="online-acceptance-email-link" disabled={!!busy} onClick={() => link(true)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-[#D35400]/50 text-[#D35400] bg-white hover:bg-orange-50 disabled:opacity-50">
          {busy === 'email' ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Email link to client
        </button>
        {st.token && (
          <button data-testid="online-acceptance-reset-link" disabled={!!busy} onClick={() => link(false, true)} title="Make a new link — the old one stops working" className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50">
            <RotateCcw size={12} /> New link
          </button>
        )}
      </div>
      {oa && (
        <div className="mt-3 text-sm text-slate-700 space-y-1" data-testid="online-acceptance-details">
          <div>Client picked online on <b>{fmt(oa.submitted_at)}</b> · Terms of Trade accepted</div>
          <ul className="list-disc pl-5">
            {(oa.quotes || []).map((q) => <li key={q.id}>{q.label}{q.appraisal_no ? ` · A${q.appraisal_no}` : ''} — {money(q.total_inc_gst)}</li>)}
          </ul>
          {oa.note && <div className="text-xs text-slate-600">Client note: “{oa.note}”</div>}
          {oa.status === 'pending_confirmation' && (
            <>
              <div className="text-xs text-amber-800">Pre-confirmation only — confirm it, then the contract is final once any deposit is paid.</div>
              <div className="flex flex-wrap gap-2 pt-1">
                {onApply && <button data-testid="online-acceptance-apply" onClick={() => onApply(oa)} className="px-3 py-1.5 text-xs font-semibold rounded bg-[#D35400] text-white hover:bg-[#A04000]">Use the client's picks below (others → declined)</button>}
                {!onApply && <button data-testid="online-acceptance-confirm" disabled={!!busy} onClick={() => review('confirmed')} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"><CheckCircle2 size={12} /> Mark confirmed</button>}
                <button data-testid="online-acceptance-dismiss" disabled={!!busy} onClick={() => review('dismissed')} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"><XCircle size={12} /> Dismiss</button>
              </div>
            </>
          )}
          {oa.reviewed_at && <div className="text-xs text-slate-500">{s?.label} by {oa.reviewed_by} · {fmt(oa.reviewed_at)}</div>}
        </div>
      )}
    </div>
  );
};
